import { useCallback, useEffect, useRef, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { ActivityIndicator, Appbar } from 'react-native-paper';
import { useSharedValue, withSpring } from 'react-native-reanimated';
import { useShallow } from 'zustand/react/shallow';

import { AppbarTextButton, ConfirmDialog, InlineBanner, ScreenAppbar } from '@/components/atoms';
import { unisonColors } from '@/theme';
import {
  MicrophoneVisual,
  RecordingControlButton,
  RecordingRemainingTime,
  RecordingStatusBadge,
  RecordingTimer,
} from '@/components/molecules/voice-recording';
import { useTimer } from '@/hooks';
import {
  getRemainingRecordingDisplay,
  getRemainingRecordingSeconds,
  getRemainingRecordingSeverity,
} from '@/models/recordingDuration';
import { useTranslation } from '@/i18n';
import { useVoiceRecordingStore } from '@/stores';

interface RecordingFullScreenProps {
  readonly contactId?: number;
  readonly meetingId?: string;
  readonly userId?: string;
  readonly accountId?: string;
  readonly onMinimize: () => void;
  readonly onMeetingCreated?: (meetingId: string, recordingSessionId: string | null) => void;
}

const SPRING_CONFIG = { damping: 12, stiffness: 120 };

export const RecordingFullScreen = ({
  contactId,
  meetingId,
  userId,
  accountId,
  onMinimize,
  onMeetingCreated,
}: RecordingFullScreenProps) => {
  const { t } = useTranslation();
  const meteringLevel = useSharedValue(0);
  const [showStopDialog, setShowStopDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    recording,
    startRecording,
    startRecordingForMeeting,
    startStandaloneRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    lowStorageWarning,
    dismissLowStorageWarning,
  } = useVoiceRecordingStore(
    useShallow((state) => ({
      recording: state.recording,
      startRecording: state.startRecording,
      startRecordingForMeeting: state.startRecordingForMeeting,
      startStandaloneRecording: state.startStandaloneRecording,
      pauseRecording: state.pauseRecording,
      resumeRecording: state.resumeRecording,
      stopRecording: state.stopRecording,
      lowStorageWarning: state.lowStorageWarning,
      dismissLowStorageWarning: state.dismissLowStorageWarning,
    })),
  );

  const { status, elapsedMs, segmentStartedAt } = recording;

  const isPaused = status === 'recording_paused';
  const isStarting = status === 'starting';
  const isStopping = status === 'stopping';
  const isActive = status === 'recording' || isPaused;

  const isOwner = useRef(status !== 'idle');
  const isActivated = useRef(status !== 'idle');
  const autoStopNavigated = useRef(false);

  useEffect(() => {
    const unsub = useVoiceRecordingStore.subscribe((state) => {
      meteringLevel.value = withSpring(state.meteringLevel, SPRING_CONFIG);
    });

    return unsub;
  }, [meteringLevel]);

  // The store's auto-stop callback only reports the meeting id, so the session id is null there.
  const navigateAfterStop = useCallback(
    (meetingId: string | null, recordingSessionId: string | null = null) => {
      autoStopNavigated.current = true;
      if (meetingId && onMeetingCreated) {
        onMeetingCreated(meetingId, recordingSessionId);
        return;
      }
      onMinimize();
    },
    [onMeetingCreated, onMinimize],
  );

  useEffect(() => {
    if (isOwner.current || isActive || !userId || !accountId) {
      return;
    }

    isOwner.current = true;

    if (meetingId != null) {
      void startRecordingForMeeting(meetingId, userId, accountId, {
        onAutoStopped: navigateAfterStop,
      });

      return;
    }

    if (contactId != null) {
      void startRecording(contactId, userId, accountId, { onAutoStopped: navigateAfterStop });

      return;
    }

    void startStandaloneRecording(userId, accountId, { onAutoStopped: navigateAfterStop });
  }, [
    userId,
    accountId,
    contactId,
    meetingId,
    isActive,
    startRecording,
    startRecordingForMeeting,
    startStandaloneRecording,
    navigateAfterStop,
  ]);

  useEffect(() => {
    if (status !== 'idle') {
      isActivated.current = true;
      return;
    }

    if (isActivated.current && !isSubmitting && !autoStopNavigated.current) {
      onMinimize();
    }
  }, [status, isSubmitting, onMinimize]);

  const elapsedSeconds = useTimer({
    isActive,
    isPaused,
    baseElapsedMs: elapsedMs,
    segmentStartTime: segmentStartedAt,
  });

  const remainingSeconds = getRemainingRecordingSeconds(elapsedSeconds);
  const remaining = getRemainingRecordingDisplay(remainingSeconds);
  const remainingLabel = t(`recording.remaining.${remaining.unit}`, { value: remaining.value });

  const handlePauseResume = () => {
    if (isSubmitting) {
      return;
    }

    if (isPaused) {
      void resumeRecording();

      return;
    }

    void pauseRecording();
  };

  const handleStop = () => {
    if (isSubmitting) {
      return;
    }

    setShowStopDialog(true);
  };

  const handleConfirmStop = async () => {
    setShowStopDialog(false);
    setIsSubmitting(true);

    // Read before stopping: the store clears the session id once the recording is saved.
    const recordingSessionId = recording.recordingSessionId ?? null;

    try {
      const meetingId = await stopRecording();
      navigateAfterStop(meetingId, recordingSessionId);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDismissStop = () => {
    setShowStopDialog(false);
  };

  const statusLabel = (() => {
    if (isPaused) {
      return t('recording.fullScreen.paused');
    }

    if (status === 'starting') {
      return t('recording.fullScreen.starting');
    }

    if (isStopping || (status === 'idle' && isSubmitting)) {
      return t('recording.fullScreen.saving');
    }

    return t('recording.fullScreen.recording');
  })();

  const pauseResumeLabel = isPaused
    ? t('recording.fullScreen.resume')
    : t('recording.fullScreen.pause');

  const pauseResumeIcon = isPaused ? 'play' : 'pause';

  return (
    <View style={styles.root}>
      <ScreenAppbar>
        <Appbar.Content title={t('recording.fullScreen.title')} />
        <AppbarTextButton
          onPress={onMinimize}
          style={styles.minimizeButton}
          textColor={unisonColors.onInfoContainer}>
          {t('recording.fullScreen.minimize')}
        </AppbarTextButton>
      </ScreenAppbar>

      {lowStorageWarning ? (
        <InlineBanner
          message={t('recording.lowStorageWarning.message')}
          variant="warning"
          onDismiss={dismissLowStorageWarning}
          testID="low-storage-warning-banner"
        />
      ) : null}
      <View style={styles.content}>
        <View style={styles.statusSection}>
          <RecordingStatusBadge isPaused={isPaused} label={statusLabel} />
          {isStarting ? (
            <ActivityIndicator
              accessibilityLabel={statusLabel}
              testID="recording-starting-indicator"
            />
          ) : null}
          <View style={styles.timerGroup}>
            <RecordingTimer elapsedSeconds={elapsedSeconds} />
            <RecordingRemainingTime
              label={remainingLabel}
              severity={getRemainingRecordingSeverity(remainingSeconds)}
            />
          </View>
        </View>

        <View style={styles.visualSection}>
          <MicrophoneVisual level={meteringLevel} isPaused={isPaused} />
        </View>

        <View style={styles.controlsSection}>
          <RecordingControlButton
            icon={pauseResumeIcon}
            label={pauseResumeLabel}
            onPress={handlePauseResume}
            disabled={isSubmitting || isStarting || isStopping}
          />
          <RecordingControlButton
            icon="square-rounded"
            label={t('recording.fullScreen.stop')}
            onPress={handleStop}
            disabled={isSubmitting || isStarting || isStopping}
            variant="stop"
          />
        </View>
      </View>

      <ConfirmDialog
        visible={showStopDialog}
        title={t('recording.stopConfirmation.title')}
        body={t('recording.stopConfirmation.body')}
        confirmLabel={t('recording.stopConfirmation.confirm')}
        dismissLabel={t('recording.stopConfirmation.dismiss')}
        onConfirm={handleConfirmStop}
        onDismiss={handleDismissStop}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: unisonColors.bgNlBase,
  },
  minimizeButton: {
    marginRight: 16,
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    marginBottom: 80,
  },
  timerGroup: {
    alignItems: 'center',
    gap: 2,
  },
  statusSection: {
    alignItems: 'center',
    gap: 9,
    paddingBottom: 16,
  },
  visualSection: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 24,
  },
  controlsSection: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 110,
    paddingTop: 24,
    paddingHorizontal: 16,
  },
});
